import React, { useState } from "react";
import { Table, Modal, Button, Form, Row, Col } from "react-bootstrap";
import PaginationComponent from "../PaginationComponent";

/**
 * Modal wyświetlany przy tworzeniu oddziału, gdy wykryto konflikty kodów pocztowych.
 * Pokazuje tabelę diff z informacją, które kody są nowe, a które istnieją w innych oddziałach.
 * Użytkownik wybiera tryb: przenieś wszystko / tylko nowe / tylko z innych terminali.
 */
const BranchCreateConflictModal = ({
  show,
  onHide,
  onConfirm,
  conflictItems = [],
  submitting,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [executionDate, setExecutionDate] = useState("");
  const [rollbackDate, setRollbackDate] = useState("");

  const conflictCount = conflictItems.filter((i) => i.isConflict).length;
  const newCount = conflictItems.filter((i) => !i.isConflict).length;

  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = conflictItems.slice(startIndex, startIndex + itemsPerPage);

  const handleConfirm = (mode) => {
    if (mode !== "OnlyNew" && !executionDate) {
      const confirmed = window.confirm(
        "Nie podano daty wykonania przeniesienia kodów.\n\n" +
        "Czy przeniesienie ma zostać wykonane natychmiastowo?\n" +
        "(Czas realizacji zależy od ilości danych do przetworzenia)"
      );
      if (!confirmed) return;
    }
    onConfirm(mode, executionDate || null, rollbackDate || null);
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Konflikty kodów pocztowych ({conflictItems.length} kodów)
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "60vh", overflowY: "auto" }}>
        <div className="mb-3">
          <p>
            Wykryto <strong>{conflictCount}</strong> kodów przypisanych do
            innych oddziałów oraz <strong>{newCount}</strong> nowych kodów
            (nieprzypisanych).
          </p>
          <p>Wybierz sposób utworzenia oddziału:</p>
        </div>

        <Table responsive striped bordered hover size="sm">
          <thead>
            <tr>
              <th>Kod pocztowy</th>
              <th>Status</th>
              <th>Obecny terminal EX</th>
              <th>Obecny terminal DR</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.length > 0 ? (
              paginatedData.map((item, index) => (
                <tr
                  key={index}
                  className={item.isConflict ? "table-warning" : "table-success"}
                >
                  <td>{item.postcode}</td>
                  <td>
                    {item.isConflict ? (
                      <span className="text-danger fw-bold">
                        ⚠ W oddziale {item.currentEx}/{item.currentDr}
                      </span>
                    ) : (
                      <span className="text-success">✓ Nowy kod</span>
                    )}
                  </td>
                  <td>{item.currentEx || "—"}</td>
                  <td>{item.currentDr || "—"}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="text-center">
                  Brak danych
                </td>
              </tr>
            )}
          </tbody>
        </Table>
        {conflictItems.length > itemsPerPage && (
          <PaginationComponent
            itemsPerPage={itemsPerPage}
            dataCount={paginatedData.length}
            onPageChange={(page) => setCurrentPage(page)}
            onItemsPerPageChange={(pageSize) => {
              setItemsPerPage(pageSize);
              setCurrentPage(1);
            }}
            itemsPerPageOptions={[100, 250, 500, 1000, 5000]}
          />
        )}

        <hr />
        <Row className="mb-3">
          <Col md={4}>
            <Form.Group controlId="conflictExecutionDate">
              <Form.Label>Data wykonania przeniesienia (opcjonalna)</Form.Label>
              <Form.Control
                type="datetime-local"
                value={executionDate}
                onChange={(e) => setExecutionDate(e.target.value)}
              />
              <Form.Text className="text-muted">
                Brak daty oznacza natychmiastowe wykonanie.
              </Form.Text>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group controlId="conflictRollbackDate">
              <Form.Label>Data wycofania (opcjonalna)</Form.Label>
              <Form.Control
                type="datetime-local"
                value={rollbackDate}
                onChange={(e) => setRollbackDate(e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer className="d-flex flex-wrap justify-content-between">
        <Button variant="secondary" onClick={onHide}>
          Anuluj — popraw ręcznie
        </Button>
        <div className="d-flex gap-2 flex-wrap">
          {newCount > 0 && (
            <Button
              variant="outline-success"
              onClick={() => handleConfirm("OnlyNew")}
              disabled={submitting}
              title="Utwórz oddział dodając tylko nowe kody (bez przenoszenia z innych oddziałów)"
            >
              {submitting ? "Zapisywanie..." : `Tylko nowe kody (${newCount})`}
            </Button>
          )}
          {conflictCount > 0 && (
            <Button
              variant="outline-warning"
              onClick={() => handleConfirm("OnlyTransfer")}
              disabled={submitting}
              title="Utwórz oddział przenosząc tylko kody z innych oddziałów (bez nowych)"
            >
              {submitting
                ? "Zapisywanie..."
                : `Tylko przeniesione (${conflictCount})`}
            </Button>
          )}
          <Button
            variant="primary"
            onClick={() => handleConfirm("TransferAll")}
            disabled={submitting}
            title="Utwórz oddział z przeniesieniem wszystkich kodów (nowe + przeniesione z innych)"
          >
            {submitting
              ? "Zapisywanie..."
              : `Przenieś wszystko (${conflictItems.length})`}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default BranchCreateConflictModal;
